import { ApiError, messageFor } from "@altek/ui/lib/problem";
import { apiErrorMessage } from "@/lib/api/error-messages";

/**
 * What went wrong, said in the words of what actually happened.
 *
 * THE CASE THIS EXISTS FOR was reported as "chat is broken": opening a conversation answered
 * "Beklenmeyen bir hata oluştu". Nothing was broken — the person was inside a SUPPORT SESSION,
 * and the server refuses personal writes there by design (`IMPERSONATION_FORBIDDEN`,
 * BACKEND_CONTRACT §7). A support session may LIST a colleague's conversations to reproduce a
 * bug, but a message it sent would appear under that colleague's name.
 *
 * So the refusal is correct and the SENTENCE was wrong, which is the more expensive half: a
 * designed, explainable refusal presented as an unexpected failure sends somebody to look for a
 * bug that is not there. Converter and the document editor already translated this code; chat
 * did not, and it is the one surface people open first.
 *
 * NOT "yetkiniz yok": the administrator holds the key. Saying otherwise sends them to ask for a
 * grant they already have.
 */
/**
 * BİLDİRİMDE `toast.errorMessage(başlık, cümle)` — `toast.error` DEĞİL (9 Ekim 2026, 4. tur).
 * `toast.error` bir HATA NESNESİ bekliyor ve açıklamayı kendisi kuruyor; ona bu dosyanın cümlesi
 * verilince cümle hata sayılmıyor ve açıklama yine «Beklenmeyen bir hata oluştu» oluyordu — bu
 * dosyanın var olma sebebi sekiz yerde birden boşa gidiyordu. Dönüştürücünün kalıbı.
 */
/** Branch on `code`, then on `details.reason` — never on `message`. BACKEND_CONTRACT §10. */
function reasonOf(error: ApiError): string | null {
  const reason = error.details?.reason;
  return typeof reason === "string" ? reason : null;
}

/**
 * Arşivdeki bir konuşmaya gönderim reddi (`CONFLICT` + `CONVERSATION_ARCHIVED`).
 *
 * Adıyla ayrı, çünkü taslak bunu bir hata olarak değil bir YÖNLENDİRME olarak ele alıyor: arşivli
 * birebir sohbete taslaktan yazılan metin kaybolmuyor, gerçek konuşmaya taşınıyor (orada engelin
 * nedeni ve «Arşivden çıkar» var).
 */
export function isConversationArchivedError(error: unknown): boolean {
  return (
    error instanceof ApiError && error.code === "CONFLICT" && reasonOf(error) === "CONVERSATION_ARCHIVED"
  );
}

/**
 * SON ÇARE `apiErrorMessage`, `messageFor` DEĞİL (KARARLAR K1).
 *
 * Kit'in haritası `QUOTA_EXCEEDED`'i tanımıyor; sohbet eki kota dolduğu için reddedildiğinde
 * buradan «Beklenmeyen bir hata oluştu» çıkıyordu — bu dosyanın var olma sebebinin aynısı, bir
 * kod aşağısında. Uygulamanın haritası kotayı rakamlarıyla söylüyor ve tanımadığı her kod için
 * zaten `messageFor`'a düşüyor, yani bu satır hiçbir şeyi kaybettirmiyor.
 */
export function chatErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return messageFor(error);
  const reason = reasonOf(error);

  if (error.code === "PERMISSION_DENIED" && reason === "IMPERSONATION_FORBIDDEN") {
    return "Destek oturumunda sohbet açılamaz. Kendi hesabınıza dönüp deneyin.";
  }

  if (error.code === "PERMISSION_DENIED" && reason === "OWNER_REQUIRED") {
    return "Bu işlem için grubun sahibi olmanız gerekiyor.";
  }

  if (error.code === "UNPROCESSABLE" && reason === "NO_MEMBERS") {
    return "Gruba en az bir kişi eklemelisiniz.";
  }

  /**
   * SON SAHİP (H7, 20 Ekim 2026). «Gruptan ayrılınamadı — İşlem tamamlanamadı.» diyordu: kişi
   * neden ayrılamadığını da, ne yapması gerektiğini de öğrenmiyordu. Sahiplik artık devredilebiliyor
   * ve cümle o yolu söylüyor. Ekran bu duruma düşmeden devir seçimini zaten açıyor; bu cümle iki
   * sekmede aynı anda yapılan bir değişikliğin sonucu için.
   */
  if (error.code === "UNPROCESSABLE" && reason === "LAST_OWNER") {
    return "Grubun tek sahibi sizsiniz. Önce sahipliği bir üyeye devredin.";
  }

  if (error.code === "LIMIT_EXCEEDED" && reason === "GROUP_TOO_LARGE") {
    return "Bir grupta en çok 200 kişi olabilir.";
  }

  if (isConversationArchivedError(error)) {
    return "Bu sohbet arşivde. Önce arşivden çıkarın.";
  }

  return apiErrorMessage(error);
}
